import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Button } from '../../shared/ui/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from '../../shared/ui/Dialog';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { resetStudentPassword } from './api';
import { CredentialCard, type StudentCredential } from './CredentialCard';
import s from './ResetPasswordDialog.module.css';

export interface ResetTarget {
  id: string;
  name: string;
  username: string;
}

interface Props {
  target: ResetTarget | null;
  open: boolean;
  onClose: () => void;
}

export function ResetPasswordDialog({ target, open, onClose }: Props): JSX.Element {
  const titleId = useId();
  const [credential, setCredential] = useState<StudentCredential | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (studentId: string) => resetStudentPassword(studentId),
    onSuccess: (result) => {
      if (!result.ok) {
        setError('Could not reset the password. Please try again.');
        return;
      }
      setCredential({
        name: target?.name ?? '',
        username: result.username || (target?.username ?? ''),
        password: result.password,
        loginUrl: window.location.origin + result.login_path,
      });
    },
    onError: (err: Error) => setError(err.message),
  });

  function handleClose(): void {
    if (mutation.isPending) return;
    setCredential(null);
    setError(null);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      labelledById={titleId}
      size="md"
      mobilePresentation="sheet"
    >
      {credential !== null ? (
        <>
          <DialogHeader
            titleId={titleId}
            title="New password ready"
            subtitle={`Share the new sign-in details with ${credential.name}.`}
            onClose={handleClose}
          />
          <DialogBody>
            <CredentialCard credential={credential} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" onClick={handleClose}>
              Done
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <DialogHeader
            titleId={titleId}
            title="Reset password?"
            subtitle={`A new password will be generated for ${target?.name ?? 'this student'}.`}
            onClose={handleClose}
          />
          <DialogBody>
            <p className={s.body}>
              {target !== null ? (
                <>
                  This creates a new password for <strong>{target.name}</strong> (username{' '}
                  <code className={s.code}>{target.username}</code>). Their current password stops
                  working right away — you’ll get the new one to share.
                </>
              ) : null}
            </p>
            {error !== null ? (
              <div className={s.banner}>
                <StatusBanner tone="error">{error}</StatusBanner>
              </div>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="button"
              loading={mutation.isPending}
              onClick={() => {
                if (target === null) return;
                setError(null);
                mutation.mutate(target.id);
              }}
            >
              Reset password
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
